import {
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogContentText,
  Grid,
  Typography,
  Backdrop,
  Box,
  Portal,
} from '@mui/material'
import { useState, useContext, useEffect, useRef } from 'react'
import { styled } from '@mui/material/styles'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import { useAuth } from 'react-oidc-context'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import {
  ItemsInCollectionDocument,
  useImportItemsToItemCollectionMutation,
  useItemCollectionFormTypeCodeLazyQuery,
} from '@kistill/client/data-access'
import { useSnackbar } from 'notistack'
import { ApolloError } from '@apollo/client'
import { FormCode } from '@kistill/shared/types'
import { ApiContext } from '../../contexts/ApiContext'
import { toUserFriendlyError, USER_FRIENDLY_NETWORK_ERROR } from '../../utils/toUserFriendlyError'
import { isValidGuid } from '../../utils/guid'
import Draggable from 'react-draggable'

function parseErrorMessage(body: string): string | null {
  if (!body?.trim()) return null
  try {
    const parsed = JSON.parse(body)
    if (typeof parsed === 'string') return parsed
    if (parsed?.message) return parsed.message
    return body
  } catch {
    return body
  }
}

export interface CreateItemsFromDocumentPopupProps {
  openCreateItemsFromDocumentPopup: boolean
  setOpenCreateItemsFromDocumentPopup: (
    deletePopup: boolean | undefined,
  ) => void
  itemCollectionGuid: string
  itemCollectionTypeGuid: string
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
})

export function CreateItemsFromDocumentPopup({
  openCreateItemsFromDocumentPopup,
  setOpenCreateItemsFromDocumentPopup,
  itemCollectionGuid,
  itemCollectionTypeGuid,
}: CreateItemsFromDocumentPopupProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [formTypeCode, setFormTypeCode] = useState<string>()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)

  const nodeRef = useRef(null)
  const { user } = useAuth()
  const apiPath = useContext(ApiContext)
  const { enqueueSnackbar } = useSnackbar()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (selectedFile) {
      setSelectedFile(selectedFile)
    }
    setIsButtonDisabled(false)
    setErrorMessage(null)
  }

  const [importItemsToItemCollectionMutation] =
    useImportItemsToItemCollectionMutation()

  const [getFormTypeCode] = useItemCollectionFormTypeCodeLazyQuery({
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const fetchFormTypeCode = async () => {
      if (itemCollectionTypeGuid && isValidGuid(itemCollectionTypeGuid)) {
        const result = await getFormTypeCode({
          variables: { itemCollectionTypeGuid: itemCollectionTypeGuid },
        })
        setFormTypeCode(result.data?.itemCollectionFormTypeCode)
      }
    }
    fetchFormTypeCode()
  }, [getFormTypeCode, itemCollectionTypeGuid])

  const handleUpload = async () => {
    setLoading(true)

    const formTypeCodeEnum: FormCode | null =
      Object.values(FormCode).find((code) => code === formTypeCode) ?? null

    if (
      !isValidGuid(itemCollectionGuid) ||
      !isValidGuid(itemCollectionTypeGuid)
    ) {
      setErrorMessage('Ógild auðkenni.')
      setLoading(false)
      return
    }

    const url = `${apiPath}/api/ItemCollections/ValidateExcelFile?itemCollectionGuid=${encodeURIComponent(itemCollectionGuid)}&formTypeEnum=${formTypeCodeEnum}`

    if (selectedFile) {
      const formData = new FormData()
      formData.append('file', selectedFile)

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
        },
        body: formData,
      })

      const responseBodyText = await response.text()

      if (response.ok) {
        const responseData = JSON.parse(responseBodyText)
        const excelImportGuid = responseData.importGuid
        handleImport(excelImportGuid)
        setErrorMessage(null)
      } else {
        const message = toUserFriendlyError(parseErrorMessage(responseBodyText))
        setErrorMessage(message ?? USER_FRIENDLY_NETWORK_ERROR)
        setIsButtonDisabled(true)
        setLoading(false)
      }
    }
  }

  const handleImport = async (excelImportGuid: string) => {
    if (formTypeCode) {
      await importItemsToItemCollectionMutation({
        variables: {
          itemCollectionGuid: itemCollectionGuid,
          formTypeEnum: formTypeCode,
          excelImportGuid: excelImportGuid,
        },
        refetchQueries: [
          {
            query: ItemsInCollectionDocument,
            variables: { itemCollectionGuid: itemCollectionGuid },
          },
        ],
        onCompleted: () => {
          enqueueSnackbar(`Skráning gagna tókst.`, { variant: 'success' })
          setOpenCreateItemsFromDocumentPopup(false)
        },
        onError: (error: ApolloError) => {
          setErrorMessage(error.message)
          setIsButtonDisabled(true)
          setLoading(false)
        },
      })
    }
  }

  const handleCancelFile = () => {
    setSelectedFile(null)
    setIsButtonDisabled(true)
  }

  return (
    <Box style={{ position: 'relative' }}>
      <Portal>
        <Backdrop
          open={openCreateItemsFromDocumentPopup}
          style={{ zIndex: 1300, position: 'fixed' }}
        />
      </Portal>
      <Draggable nodeRef={nodeRef}>
        <div ref={nodeRef}>
        <Dialog
          fullWidth
          hideBackdrop={true}
          maxWidth='sm'
          open={openCreateItemsFromDocumentPopup}
          PaperProps={{
            sx: { maxWidth: 'none', width: '70%' },
          }}
        >
          <DialogTitle sx={{ pt: 5 }}>Fjöldaskrá eintök með skjali</DialogTitle>
          <DialogContent dividers>
            <DialogContentText
              component='div'
              sx={{ my: 2 }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              {!selectedFile ? (
                <Button
                  component='label'
                  variant='contained'
                  startIcon={<CloudUploadIcon />}
                >
                  Velja skjal til innlestrar
                  <VisuallyHiddenInput
                    type='file'
                    onChange={handleChange}
                    accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  />
                </Button>
              ) : (
                <Grid>
                  <Grid container justifyContent='center' alignItems='center'>
                    <Grid>
                      <DescriptionIcon fontSize='medium' />
                    </Grid>
                    <Grid
                      item
                      sx={{ mr: 2, ml: 1 }}
                      style={{ fontSize: 'large' }}
                    >
                      {selectedFile.name}
                    </Grid>
                    <Grid item>
                      <Button onClick={() => handleCancelFile()}>
                        <CloseIcon fontSize='medium' />
                      </Button>
                    </Grid>
                  </Grid>
                  {errorMessage && (
                    <Typography
                      sx={{ color: 'red', textAlign: 'center', pt: 2 }}
                    >
                      {errorMessage}
                    </Typography>
                  )}
                </Grid>
              )}
            </DialogContentText>
            <DialogActions sx={{ mt: 4, px: 0 }}>
              <Button
                sx={{ mr: 2 }}
                variant='contained'
                onClick={() => setOpenCreateItemsFromDocumentPopup(false)}
              >
                Til Baka
              </Button>
              <Button
                variant='contained'
                color='secondary'
                onClick={() => handleUpload()}
                disabled={isButtonDisabled || loading}
              >
                Skrá
              </Button>
            </DialogActions>
          </DialogContent>
        </Dialog>
        </div>
      </Draggable>
    </Box>
  )
}
